import React, { useState } from "react";
import { Download, ArrowRight, Handshake, ShieldCheck, Sparkles } from "lucide-react";
import PageHero from "../components/PageHero";
import useReveal from "../hooks/useReveal";
import usePageMeta from "../hooks/usePageMeta";
import { api } from "../lib/api";
import { toast } from "sonner";
import { PARTNERS } from "../data/site";

const initial = { organisation: "", contact_name: "", email: "", phone: "", message: "" };

export default function Partners() {
  useReveal();
  usePageMeta({ title: "Partners & Referrers", description: "Partner with Thrive Bridge Mental Wellness CIC to reach those most at risk." });

  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/partners/enquiry", form);
      toast.success("Thank you. A member of the team will be in touch.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Partners & referrers"
        title="Partners and referrers"
        lead="We work with statutory and voluntary partners across the UK to reach people who need support."
      />

      <section className="container-page section-spacing grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-7 fade-up">
          <span className="text-xs font-semibold tracking-widest uppercase text-[var(--tb-primary)]">Who we work with</span>
          <h2 className="font-serif-display mt-3 text-3xl md:text-4xl text-[var(--tb-text)]">
            Who we work with
          </h2>
          <p className="mt-4 text-lg text-[var(--tb-text-2)] text-pretty">
            We partner with housing associations, Jobcentre Plus, local authorities, voluntary organisations,
            community groups, faith-based organisations and the NHS.
          </p>
          <ul className="mt-6 grid grid-cols-2 gap-3">
            {PARTNERS.map((p) => (
              <li key={p} className="rounded-xl border border-[var(--tb-border)] bg-white px-4 py-3 text-sm text-[var(--tb-text-2)]">{p}</li>
            ))}
          </ul>
        </div>

        <aside className="lg:col-span-5 fade-up">
          <div className="bg-[var(--tb-surface)] border border-[var(--tb-border)] rounded-3xl p-7">
            <Sparkles className="h-7 w-7 text-[var(--tb-primary)] mb-3" />
            <h3 className="font-serif-display text-2xl text-[var(--tb-text)] mb-4">Why partner with us</h3>
            <ul className="space-y-3 text-[var(--tb-text-2)]">
              <li className="flex gap-2"><ShieldCheck className="h-5 w-5 text-[var(--tb-primary)] shrink-0 mt-0.5" /> Trauma-informed, culturally responsive practice</li>
              <li className="flex gap-2"><Handshake className="h-5 w-5 text-[var(--tb-primary)] shrink-0 mt-0.5" /> Established referral pathways and clear outcomes</li>
              <li className="flex gap-2"><Sparkles className="h-5 w-5 text-[var(--tb-primary)] shrink-0 mt-0.5" /> Co-designed with people of lived experience</li>
            </ul>
            <a
              href="#"
              data-testid="referral-pathway-download"
              onClick={(e) => { e.preventDefault(); toast.message("Referral pathway PDF coming soon."); }}
              className="mt-6 inline-flex items-center gap-2 rounded-full border-2 border-[var(--tb-primary)] text-[var(--tb-primary)] hover:bg-white px-5 py-3 font-semibold transition-colors"
            >
              <Download className="h-4 w-4" /> Download referral pathway (PDF)
            </a>
          </div>
        </aside>
      </section>

      <section id="enquiry" className="bg-[var(--tb-surface)] border-y border-[var(--tb-border)]">
        <div className="container-page section-spacing">
          <div className="max-w-2xl mb-8 fade-up">
            <span className="text-xs font-semibold tracking-widest uppercase text-[var(--tb-primary)]">Partnership enquiry</span>
            <h2 className="font-serif-display mt-3 text-3xl md:text-4xl lg:text-5xl text-[var(--tb-text)]">
              Partnership enquiry
            </h2>
            <p className="mt-3 text-[var(--tb-text-2)] text-pretty">
              Tell us about your organisation and what you&rsquo;re looking for. We&rsquo;ll reply within 5 working days.
            </p>
          </div>
          <form
            onSubmit={submit}
            data-testid="partner-enquiry-form"
            className="fade-up bg-white border border-[var(--tb-border)] rounded-3xl p-6 md:p-10 grid gap-5 md:grid-cols-2"
          >
            <Field label="Organisation" required>
              <input required data-testid="partner-organisation-input" value={form.organisation} onChange={(e) => setForm({ ...form, organisation: e.target.value })} className={inputCls} />
            </Field>
            <Field label="Your name" required>
              <input required data-testid="partner-name-input" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className={inputCls} />
            </Field>
            <Field label="Email" required>
              <input required type="email" data-testid="partner-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
            </Field>
            <Field label="Phone (optional)">
              <input data-testid="partner-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
            </Field>
            <Field label="How can we partner?" required className="md:col-span-2">
              <textarea required rows={5} data-testid="partner-message-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${inputCls} resize-y`} />
            </Field>
            <div className="md:col-span-2 flex justify-end">
              <button
                type="submit"
                disabled={loading}
                data-testid="partner-submit-button"
                className="inline-flex items-center gap-2 rounded-full bg-[var(--tb-primary)] hover:bg-[var(--tb-primary-hover)] text-white px-6 py-3.5 font-semibold disabled:opacity-60"
              >
                {loading ? "Sending…" : "Send enquiry"} <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </form>
        </div>
      </section>
    </>
  );
}

const inputCls =
  "w-full rounded-xl border border-[var(--tb-border)] bg-white px-4 py-3 text-[var(--tb-text)] placeholder:text-[var(--tb-muted)] focus:border-[var(--tb-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--tb-primary)]/20 transition-all";

function Field({ label, hint, required, children, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-sm font-semibold text-[var(--tb-text)] mb-2">
        {label} {required && <span className="text-[var(--tb-crisis)]" aria-hidden="true">*</span>}
      </label>
      {children}
      {hint && <p className="mt-1.5 text-xs text-[var(--tb-muted)]">{hint}</p>}
    </div>
  );
}
